/**
 * StormOfBladesSkillEffect — Blades tree slash-path apotheosis.
 *
 * Five conjured blades orbit the player for 12 seconds, dealing contact
 * damage to enemies within 1.5 tiles each tick. Every 1.5 seconds one
 * blade detaches and strikes the nearest enemy for Flurry damage before
 * returning to orbit. Blade Dance stacks at activation time extend
 * duration by 1 second each.
 *
 * Duration-limited toggle: auto-deactivates when the timer expires.
 * Very long cooldown tier (4000ms).
 *
 * Slot cost: 1 (per design-decisions.md — all apotheosis nodes cost 1
 * slot, same as every other active skill, overriding the spec's "2 slots"
 * text).
 *
 * All damage is blade-type: crit-eligible, Whetstone-eligible.
 *
 * Extends ToggleSkillEffect for active/cooldown lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Storm of Blades
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StormOfBladesDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly bladeDance: {
    /** Current Blade Dance stack count (read at activation, not live). */
    readonly stacks: number;
  };
  readonly combat: {
    /** Apply per-tick contact damage to all enemies within range. Returns count hit. */
    applyOrbitalContactDamage(attackType: string, rangeTiles: number): number;
    /** Fire a targeted strike at the nearest enemy. Returns count hit (0 or 1). */
    startDetachStrike(attackType: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StormOfBladesConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly detachIntervalMs: number;
  readonly bladeDanceExtensionPerStackMs: number;
  readonly contactRangeTiles: number;
  readonly bladeCount: number;
}

const DEFAULT_CONFIG: StormOfBladesConfig = {
  cooldownMs: 4000,                       // very_long cooldown tier
  baseDurationMs: 12000,                  // 12 seconds base
  detachIntervalMs: 1500,                 // one blade detaches every 1.5s
  bladeDanceExtensionPerStackMs: 1000,    // +1s per Blade Dance stack
  contactRangeTiles: 1.5,                 // orbital contact damage radius
  bladeCount: 5,                          // five conjured blades
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StormOfBladesSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'storm_of_blades';

  private readonly deps: StormOfBladesDeps;
  private readonly config: StormOfBladesConfig;

  // Active-state timers
  private _durationRemaining = 0;
  private _detachTimer = 0;

  // Resolved / snapshot values
  private _resolvedDurationMs = 0;
  private _bladeDanceStacksAtActivation = 0;

  // Counters for assertions / game-layer queries
  private _detachStrikesFired = 0;
  private _contactDamageApplications = 0;
  private _totalContactEnemiesHit = 0;
  private _totalDetachEnemiesHit = 0;
  private _activationCount = 0;

  constructor(deps: StormOfBladesDeps, config: Partial<StormOfBladesConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get durationRemaining(): number { return this._durationRemaining; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get bladeDanceStacksAtActivation(): number { return this._bladeDanceStacksAtActivation; }
  get detachStrikesFired(): number { return this._detachStrikesFired; }
  get contactDamageApplications(): number { return this._contactDamageApplications; }
  get totalContactEnemiesHit(): number { return this._totalContactEnemiesHit; }
  get totalDetachEnemiesHit(): number { return this._totalDetachEnemiesHit; }
  get activationCount(): number { return this._activationCount; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Snapshot Blade Dance stacks at activation time
    this._bladeDanceStacksAtActivation = this.deps.bladeDance.stacks;

    // Compute duration: base + extension per stack
    this._resolvedDurationMs = this.config.baseDurationMs +
      this._bladeDanceStacksAtActivation * this.config.bladeDanceExtensionPerStackMs;

    this._durationRemaining = this._resolvedDurationMs;
    this._detachTimer = 0;
    this._detachStrikesFired = 0;
    this._contactDamageApplications = 0;
    this._totalContactEnemiesHit = 0;
    this._totalDetachEnemiesHit = 0;
    this._activationCount++;

    this._isActive = true;
    return true;
  }

  protected onDeactivate(): void {
    this._durationRemaining = 0;
    this._detachTimer = 0;
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    // Apply orbital contact damage every tick
    const contactHits = this.deps.combat.applyOrbitalContactDamage(
      'storm_of_blades',
      this.config.contactRangeTiles,
    );
    this._totalContactEnemiesHit += contactHits;
    this._contactDamageApplications++;

    // Tick the detach timer and fire strikes at intervals
    this._detachTimer += delta;
    while (this._detachTimer >= this.config.detachIntervalMs) {
      this._detachTimer -= this.config.detachIntervalMs;
      this.fireDetachStrike();
    }

    // Decrement duration
    this._durationRemaining = Math.max(0, this._durationRemaining - delta);
    if (this._durationRemaining <= 0) {
      this.deactivate();
    }
  }

  destroy(): void {
    this._durationRemaining = 0;
    this._detachTimer = 0;
    super.destroy();
  }

  // --- Internal ---

  private fireDetachStrike(): void {
    const enemiesHit = this.deps.combat.startDetachStrike('flurry');
    this._totalDetachEnemiesHit += enemiesHit;
    this._detachStrikesFired++;
  }
}
